<template>
  <div ref="formsContainer" class="formsContainer">
    <component
      v-for="form in forms"
      id="object-form"
      :key="form.id"
      :is="openedFormComponent(form.openedForm)"
      :style="{ zIndex: form.zIndex }"
      :object-layer="form.openedForm"
      :action-type="form.formAction"
      :data-received="form.selectedObjectData"
      :wkt="form.wkt"
      :street-view="form.showStreetView"
      :link-object-to-pathway="form.linkObjectToPathway"
      :link-pathway-to-pathway="form.linkPathwayToPathway"
      :pathway-nodes="form.pathwayNodes"
      :technical-point-data="form.technicalPointData"
      :technical-site-data="form.technicalSiteData"
      :cable-data="form.cable"
      :lineStringWKT="form.lineStringWKT"
      :objectNodes="form.objectNodes"
      :slack-data="form.slackData"
      :previous-pos-x="form.objectFormPosition.x"
      :previous-pos-y="form.objectFormPosition.y"
      :closureTechPointData="closureTechPointData"
      :techPointGeom="techPointGeom"
      :nextForm="nextForm"
      :creatingBulkTP="creatingBulkTp"
      :bulkTPCoordinates="bulkTpCoordinates"
      :blockageObject="blockageObject"
      @close="closeFormWithID(form.id)"
      @update-technical-point-data="updateTechnicalPointData(form.id, $event)"
      @update-geoserver="updateGeoserver"
      @changeViewModeOfObject="changeViewModeOfObject($event, form)"
      @create-technical-local="createTechnicalLocal"
      @click.native="bringFormToFront(form.id)"
      @open-next-form="openNextForm($event)"
      @clear-next-form="clearNextForm"
      @delete-measurement-area="$emit('delete-measurement-area')"
      @open-sync-dialog="
        syncDialog = true;
        assetReservationToSyncID = $event;
      "
      @update-tech-point-data="$emit('update-tech-point-data', $event)"
      @update-pathway-data="$emit('update-pathway-data', $event)"
      @openDetails="$emit('openDetails')"
      @refresh="refresh(form.id,form.openedForm, form.formAction)"
      @openClosureToExport="$emit('openClosureToExport')"
    />
    <app-dialog-box
      :open="syncDialog"
      :title="$t('SyncData')"
      :content="$t('SyncDataMessage')"
      :main-action-label="$t('No')"
      @mainAction="closeSync"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="confirmSync"
    />
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { mapActions } from "vuex";
import CableForm from "./ObjectForms/CableForm";
import ClosureForm from "./ObjectForms/ClosureForm";
import TechnicalLocalForm from "./ObjectForms/TechnicalLocalForm";
import BuildingForm from "./ObjectForms/BuildingForm";
import WorkPointForm from "./ObjectForms/WorkPointForm";
import PNForm from "./ObjectForms/PNForm";
import COForm from "./ObjectForms/COForm";
import PathwayForm from "./ObjectForms/PathwayForm";
import PoleForm from "./ObjectForms/PoleForm";
import ManholeForm from "./ObjectForms/ManholeForm";
import CabinetForm from "./ObjectForms/CabinetForm";
import TobyBoxForm from "./ObjectForms/TobyBoxForm";
import DetailForm from "./ObjectForms/DetailForm";
import PNAreaForm from "./ObjectForms/PNAreaForm";
import COAreaForm from "./ObjectForms/COAreaForm";
import OtherAreaForm from "./ObjectForms/OtherAreaForm";
import ClosureAreaForm from "./ObjectForms/ClosureAreaForm";
import AnnotationPointForm from "./ObjectForms/AnnotationPointForm";
import FieldCableForm from "./ObjectForms/FieldCableForm";
import SlackForm from "./ObjectForms/SlackForm";
import SiteForm from "./ObjectForms/SiteForm";
import AssetReservationForm from "@/components/Home/Forms/ObjectForms/AssetReservationForm";
import ChangeLogForm from "./ChangeLogForm/ChangeLogForm";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import BundleForm from "./ObjectForms/BundleForm";
import VirtualForm from "./ObjectForms/VirtualForm";
import BlockageForm from "./ObjectForms/BlockageForm";

export default {
  name: "MultiObjectForm",

  components: {
    SlackForm,
    CableForm,
    ClosureForm,
    TechnicalLocalForm,
    BuildingForm,
    PNForm,
    COForm,
    PathwayForm,
    PoleForm,
    ManholeForm,
    CabinetForm,
    TobyBoxForm,
    DetailForm,
    PNAreaForm,
    COAreaForm,
    OtherAreaForm,
    WorkPointForm,
    ClosureAreaForm,
    AnnotationPointForm,
    FieldCableForm,
    SiteForm,
    AssetReservationForm,
    ChangeLogForm,
    AppDialogBox,
    BundleForm,
    VirtualForm,
    BlockageForm
  },

  props: {
    /**
     * The next form to be opened
     */
    nextForm: {
      type: String,
      required: false,
      default: null
    },

    /**
     * Indicates if the bulk creation is active
     */
    creatingBulkTp: {
      type: Boolean,
      required: false,
      default: false
    },

    /**
     * Indicates the coordinates of the bulk tech points
     */
    bulkTpCoordinates: {
      type: Array,
      required: false,
      default: []
    },

    /**
     * Indicates the type of the blockage object
     */
    blockageObject: {
      type: Object,
      required: false,
    }
  },

  data() {
    return {
      objectFormPosition: {
        x: undefined,
        y: undefined
      },

      closureTechPointData: null,
      techPointGeom: null,

      /**
       * Indicates if the dialog about the synchronization is open or not
       */
      syncDialog: false,
      /**
       * ID of the asset reservation to sync with the external service
       */
      assetReservationToSyncID: null
    };
  },

  computed: {
    forms() {
      return this.$store.getters.getForms;
    },
    formId() {
      return this.$store.getters.getFormId;
    },
    formZIndex() {
      return this.$store.getters.getFormZIndex;
    },

    selectedObjectData() {
      return this.$store.getters.selectedObjectData;
    }
  },

  mounted() {
    bus.$on("update-object-form-position", coordinates => {
      this.objectFormPosition.x = coordinates.x;
      this.objectFormPosition.y = coordinates.y;
    });
  },

  methods: {
    ...mapActions([
      "CHANGE_VIEW_MODE_OF_FORM",
      "ADD_NEW_FORM",
      "REMOVE_FORM_BY_ID",
      "UPDATE_FORM_Z_INDEX_TO_FRONT",
      "updateFormTechnicalPointData",
      "fetchCablesPassingInTechPoint",
      "syncWithExternalService"
    ]),

    /** Emits an event to close this component's window **/
    closeForm() {
      this.$emit("close");
    },
    refresh(formId, layer, action){
      this.closeFormWithID(formId);
      this.$emit('refresh', layer, action);
    },

    /** Emits an event update the map **/
    updateGeoserver() {
      this.$emit("update-geoserver");
    },

    updateTechnicalPointData(formID, technicalPointData) {
      this.updateFormTechnicalPointData({
        formID: formID,
        technicalPointData: technicalPointData
      });
    },

    changeViewModeOfObject(viewType, form) {
      this.CHANGE_VIEW_MODE_OF_FORM({
        formId: form.id,
        viewType: viewType
      });
    },

    createTechnicalLocal(technicalSite, technicalSiteData) {
      this.$emit("create-technical-local", technicalSite, technicalSiteData);
    },

    openNewForm(
      openedForm,
      formAction,
      wkt,
      showStreetView,
      linkObjectToPathway,
      linkPathwayToPathway,
      pathwayNodes,
      technicalPointData,
      technicalSiteData,
      cable,
      lineStringWKT,
      objectNodes,
      slackData,
      isMeasurementArea = null
    ) {
      if (this.validateNewForm(openedForm, formAction)) {
        let form = {
          id: this.formId,
          zIndex: this.formZIndex,
          openedForm: openedForm,
          formAction: formAction,
          selectedObjectData: this.selectedObjectData,
          wkt: wkt,
          showStreetView: showStreetView,
          linkObjectToPathway: linkObjectToPathway,
          linkPathwayToPathway: linkPathwayToPathway,
          pathwayNodes: pathwayNodes,
          technicalPointData: technicalPointData,
          technicalSiteData: technicalSiteData,
          cable: cable,
          lineStringWKT: lineStringWKT,
          objectNodes: objectNodes,
          slackData: slackData,
          objectFormPosition: {
            x: this.nextObjectFormPosition(this.objectFormPosition.x),
            y: this.nextObjectFormPosition(this.objectFormPosition.y)
          },
          isMeasurementArea: isMeasurementArea
        };
        this.ADD_NEW_FORM(form);
      }
    },

    nextObjectFormPosition(position) {
      if (this.forms.length === 0) {
        return position;
      } else {
        return position ? position + 20 : undefined;
      }
    },

    closeFormWithID(id) {
      this.REMOVE_FORM_BY_ID(id);
    },

    validateNewForm(openedForm, formAction) {
      let openForm = true;
      this.forms.forEach(form => {
        if (formAction === "create" && form.formAction === "create") {
          openForm = false;
          this.$toasted.global.toast_error(
            "You mustn't have more than 1 form opened in mode create!"
          );
        }

        if (this.selectedObjectData) {
          let objectUuidKey = Object.keys(form.selectedObjectData).find(key => {
            return key.includes("uuid");
          });
          if (
            objectUuidKey &&
            form.selectedObjectData[objectUuidKey] ===
              this.selectedObjectData[objectUuidKey]
          ) {
            if (form.openedForm === openedForm) {
              openForm = false;
              this.$toasted.global.toast_error("You already opened this form!");
            }
          }
        }
      });
      return openForm;
    },

    openedFormComponent(openedForm) {
      let objectComponent;
      if (openedForm !== "" && openedForm !== "Workspace") {
        switch (openedForm) {
          case "fc_building":
            objectComponent = "Building";
            break;
          case "fc_pole":
            objectComponent = "Pole";
            break;
          case "fc_manhole":
            objectComponent = "Manhole";
            break;
          case "fc_cabinet":
            objectComponent = "Cabinet";
            break;
          case "fc_toby_box":
            objectComponent = "TobyBox";
            break;
          case "fc_pathway":
            objectComponent = "Pathway";
            break;
          case "fc_closure":
            objectComponent = "Closure";
            break;
          case "fc_cable":
            objectComponent = "Cable";
            break;
          case "fc_workpoint":
            objectComponent = "WorkPoint"
            break;
          case "fc_tower":
            objectComponent = "Tower";
            break;
          case "fc_technical_local_fdp":
          case "fc_technical_local_mdu":
          case "fc_technical_local_olt":
          case "fc_technical_local_site":
          case "fc_technical_local":
            objectComponent = "TechnicalLocal";
            break;
          case "fc_fiber_distribution_point":
            objectComponent = "PN";
            break;
          case "fc_optical_line_termination":
            objectComponent = "CO";
            break;
          case "fc_fdp_zone":
            objectComponent = "PNArea";
            break;
          case "fc_olt_zone":
            objectComponent = "COArea";
            break;
          case "fc_annotation_zone":
            objectComponent = "OtherArea";
            break;
          case "fc_closure_zone":
            objectComponent = "ClosureArea";
            break;
          case "fc_annotation_point":
            objectComponent = "AnnotationPoint";
            break;
          case "fc_field_cable":
            objectComponent = "FieldCable";
            break;
          case "fc_slack":
            objectComponent = "Slack";
            break;
          case "fc_site":
            objectComponent = "Site";
            break;
          case "fc_asset_reservation":
            objectComponent = "AssetReservation";
            break;
          case "fc_microduct_bundle":
            objectComponent = "Bundle";
            break;
          case "fc_virtual":
            objectComponent = "Virtual";
            break;
          case "fc_blockage":
            objectComponent = "Blockage";
            break;
          case "Detail":
            objectComponent = "Detail";
            break;
          case "ChangeLog":
            objectComponent = "ChangeLog";
            break;
        }
        return objectComponent + "Form";
      } else return "";
    },

    bringFormToFront(formId) {
      let form = this.forms.find(form => formId === form.id);
      if (form) this.UPDATE_FORM_Z_INDEX_TO_FRONT(formId);
    },

    /**
     * Opens the form corresponding to the nextForm variable layer
     */
    openNextForm(event = null) {
      if (event) {
        this.closureTechPointData = event.createdTechPointData;
        this.techPointGeom = event.techPointGeom;
        this.fetchCablesPassingInTechPoint(this.closureTechPointData.tp_uuid)
          .then(cables => {
            if (cables && cables.length > 0) {
              this.$emit("open-cables-split-dialog", this.closureTechPointData);
            } else {
              this.openNewForm(this.nextForm, "create");
            }
          })
          .catch(e => {
            console.error(e);
          });
      } else {
        this.openNewForm(this.nextForm, "create");
      }
    },

    clearNextForm() {
      this.$emit("clear-next-form");
    },

    /**
     * Sends a request to the BE to synchronize with the external service
     */
    async confirmSync() {
      try {
        await this.syncWithExternalService({
          assetReservationId: this.assetReservationToSyncID
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.closeSync();
      }
    },

    /**
     * Closes the sync dialog and clears the asset reservation id
     */
    closeSync() {
      this.syncDialog = false;
      this.assetReservationToSyncID = null;
    }
  }
};
</script>

<style scoped>
.formsContainer {
  position: absolute;
  height: 100%;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/MultiObjectForm.vue